export const sharedResultParameterNames = ['tool', 'compressor', 'mode', 'qty'] as const

export type SharedConsumptionMode = 'average' | 'load'

export type SharedRecordIdentity = {
  id: string
  model: string
  legacyShareAliases?: readonly string[]
}

export type SharedComparisonState = {
  toolId: string
  toolModel: string
  compressorId: string
  compressorModel: string
  mode: SharedConsumptionMode
  quantity: number
}

export type SharedComparisonInput = {
  toolId: string
  compressorId: string
  mode: SharedConsumptionMode
  quantity: number
}

export type SharedComparisonOptions = {
  tools: readonly SharedRecordIdentity[]
  compressors: readonly SharedRecordIdentity[]
}

export type MatcherEvent = {
  name: 'shared_result_viewed' | 'utility_completed' | 'result_link_copied'
  details: {
    tool_id: string
    tool_model: string
    compressor_id: string
    compressor_model: string
    mode: SharedConsumptionMode
    quantity: number
    entry?: 'form' | 'shared_url'
  }
}

const quantityPattern = /^(?:[1-9]|1\d|20)$/

function resolveRecord(value: string, records: readonly SharedRecordIdentity[]) {
  return records.find((record) => record.id === value)
    ?? records.find((record) => record.legacyShareAliases?.includes(value))
    ?? null
}

export function createSharedComparisonState(
  value: SharedComparisonInput,
  options: SharedComparisonOptions,
): SharedComparisonState | null {
  const tool = options.tools.find((record) => record.id === value.toolId)
  const compressor = options.compressors.find((record) => record.id === value.compressorId)
  if (!tool || !compressor) return null
  if (value.mode !== 'average' && value.mode !== 'load') return null
  if (!Number.isInteger(value.quantity) || value.quantity < 1 || value.quantity > 20) return null
  return {
    toolId: tool.id,
    toolModel: tool.model,
    compressorId: compressor.id,
    compressorModel: compressor.model,
    mode: value.mode,
    quantity: value.quantity,
  }
}

export function parseSharedComparison(
  search: string,
  options: SharedComparisonOptions,
): SharedComparisonState | null {
  const params = new URLSearchParams(search)

  if (sharedResultParameterNames.some((name) => params.getAll(name).length !== 1)) return null

  const tool = resolveRecord(params.get('tool') ?? '', options.tools)
  const compressor = resolveRecord(params.get('compressor') ?? '', options.compressors)
  const mode = params.get('mode') ?? ''
  const quantityValue = params.get('qty') ?? ''

  if (!tool || !compressor) return null
  if (mode !== 'average' && mode !== 'load') return null
  if (!quantityPattern.test(quantityValue)) return null

  return createSharedComparisonState(
    {
      toolId: tool.id,
      compressorId: compressor.id,
      mode,
      quantity: Number(quantityValue),
    },
    options,
  )
}

function setSharedParameters(url: URL, state: SharedComparisonState) {
  url.searchParams.set('tool', state.toolId)
  url.searchParams.set('compressor', state.compressorId)
  url.searchParams.set('mode', state.mode)
  url.searchParams.set('qty', String(state.quantity))
  url.hash = 'matcher'
}

export function browserPathWithSharedComparison(currentHref: string, state: SharedComparisonState) {
  const url = new URL(currentHref)
  setSharedParameters(url, state)
  return `${url.pathname}${url.search}${url.hash}`
}

export function browserPathWithoutSharedComparison(currentHref: string) {
  const url = new URL(currentHref)
  for (const name of sharedResultParameterNames) url.searchParams.delete(name)
  return `${url.pathname}${url.search}${url.hash}`
}

export function publicSharedComparisonUrl(baseUrl: string, state: SharedComparisonState) {
  const url = new URL('/', baseUrl)
  setSharedParameters(url, state)
  return url.toString()
}

function eventDetails(state: SharedComparisonState) {
  return {
    tool_id: state.toolId,
    tool_model: state.toolModel,
    compressor_id: state.compressorId,
    compressor_model: state.compressorModel,
    mode: state.mode,
    quantity: state.quantity,
  }
}

export function sharedResultViewedEvent(state: SharedComparisonState): MatcherEvent {
  return { name: 'shared_result_viewed', details: eventDetails(state) }
}

export function utilityCompletedEvent(
  state: SharedComparisonState,
  entry: 'form' | 'shared_url',
): MatcherEvent {
  return { name: 'utility_completed', details: { ...eventDetails(state), entry } }
}

export function resultLinkCopiedEvent(state: SharedComparisonState): MatcherEvent {
  return { name: 'result_link_copied', details: eventDetails(state) }
}

export async function copySharedComparisonLink(
  writeText: ((value: string) => Promise<void>) | undefined,
  url: string,
): Promise<'copied' | 'failed'> {
  if (!writeText) return 'failed'
  try {
    await writeText(url)
    return 'copied'
  } catch {
    return 'failed'
  }
}
